import { useCallback, useEffect, useRef, useState } from 'react';
import { isExtensionSwIgnoredMessage } from '@/extension/sw-runtime-messages';
import { isExtensionApp } from '@/utils/platform';
import Logger from '@/utils/logger';

// ---------------------------------------------------------------------------
// Shared types for message payloads
// ---------------------------------------------------------------------------

type MessageData = Record<string, unknown>;
type MessageCallback = (data: unknown) => void;

/** Runtime message shape received from the background service worker. */
interface RuntimeMessage {
  action?: string;
  type?: string;
  data?: MessageData & { callbackId?: string | number };
}

// Module-level loggers — stable references, safe to use inside useCallback/useEffect.
const _utilLogger = new Logger({ tag: 'WEB_EXTENSION_UTIL' });
const _listenerLogger = new Logger({ tag: 'WEB_EXTENSION_MESSAGE_LISTENER' });

// ---------------------------------------------------------------------------
// Singleton extension-detection utility
// ---------------------------------------------------------------------------

type ExtensionState = boolean | null;

const _ext: { installed: ExtensionState } = {
  installed: typeof window !== 'undefined' && isExtensionApp() ? true : null,
};
let _pendingPromise: Promise<boolean> | null = null;
let _resolvePending: ((value: boolean) => void) | null = null;
let _timeoutId: ReturnType<typeof setTimeout> | null = null;

function _cleanup() {
  if (_timeoutId) clearTimeout(_timeoutId);
  _pendingPromise = null;
  _resolvePending = null;
  _timeoutId = null;
}

/**
 * Returns a promise that resolves to `true`/`false` once the extension is
 * detected (tabregistered event) or the 800 ms timeout fires.
 * All callers share the same promise — no duplicate timers ever created.
 */
export const hasExtension = (): Promise<boolean> => {
  if (isExtensionApp()) {
    _ext.installed = true;
    return Promise.resolve(true);
  }
  if (_ext.installed !== null) return Promise.resolve(_ext.installed);
  if (_pendingPromise) return _pendingPromise;

  _pendingPromise = new Promise<boolean>((resolve) => {
    _resolvePending = resolve;
    _timeoutId = setTimeout(() => {
      if (_ext.installed === null) _ext.installed = false;
      const resolve = _resolvePending;
      _cleanup();
      resolve?.(_ext.installed as boolean);
    }, 800);
  });

  return _pendingPromise;
};

/**
 * Called by the tabregistered handler to mark the extension as present.
 * Also called internally by the timeout to mark it absent.
 */
export const setExtensionInstalled = (value: boolean): void => {
  _ext.installed = value;
  if (_resolvePending) {
    const resolve = _resolvePending;
    _cleanup();
    resolve(value);
  }
};

/** Synchronous read of the current detection state (null = still pending). */
export const getExtensionInstalled = (): boolean | null => _ext.installed;

/**
 * React hook — returns null while detection is pending, then true/false.
 * Components using this will re-render exactly once when detection resolves.
 * Keeping UNSAVED visible while null prevents premature sidebar flicker.
 */
export const useHasExtension = (): boolean | null => {
  const [detected, setDetected] = useState<boolean | null>(() => {
    if (_ext.installed !== null) return _ext.installed;
    return isExtensionApp() ? true : null;
  });
  useEffect(() => {
    if (isExtensionApp()) {
      setExtensionInstalled(true);
      setDetected(true);
      return;
    }
    if (_ext.installed !== null) {
      setDetected(_ext.installed);
      return;
    }
    hasExtension().then(setDetected);
  }, []);
  return detected;
};

// ---------------------------------------------------------------------------

export interface ExtensionUtilContextType {
  sendCBMessage: (action: string, param: MessageData | MessageCallback, callBack?: MessageCallback) => void;
  sendMessage: (action: string, param?: MessageData, cb?: MessageCallback) => void;
}

export interface UseExtensionMessageListenerOptions {
  /** Called when a message with type "postMessageTab" is received (e.g. sessions data). */
  onSessions?: (data: unknown) => void;
  /** Called once when the content script confirms the extension is installed (tabregistered). */
  onExtensionDetected?: () => void;
  /** Optional logger; defaults to console. */
  logger?: { log: (...args: unknown[]) => void; error: (...args: unknown[]) => void };
}

declare global {
  interface Window {
    troveApp?: {
      hasExtension?: boolean;
      isExtension?: () => boolean;
      extCallback?: Record<string | number, MessageCallback>;
      listener?: {
        postMessage?: Record<string | number, MessageCallback>;
        sessions?: (data: unknown) => void;
      };
    };
    chrome?: {
      runtime?: {
        sendMessage: (msg: { action: string; data?: MessageData }, cb?: MessageCallback) => void;
        lastError?: unknown;
        onMessage: {
          addListener: (listener: (msg: unknown) => void) => void;
          removeListener: (listener: (msg: unknown) => void) => void;
        };
      };
    };
  }
}

/**
 * Hook to communicate from the React app to the browser extension via the content script.
 * Use sendMessage for fire-and-forget or one-shot response; use sendCBMessage when you need
 * a callback from the extension.
 */
export const useExtensionUtil = (): ExtensionUtilContextType => {
  const sendMessage = useCallback((action: string, param?: MessageData, cb?: MessageCallback) => {
    _utilLogger.log('sendMessage calling....', action, param);
    if (!window.troveApp?.isExtension?.()) {
      window.postMessage({ from: 'troves', action: 'bg', data: { action, data: param } }, '*');
    } else if (window.chrome?.runtime) {
      const data = param ?? {};
      try {
        if (cb) {
          window.chrome.runtime.sendMessage({ action, data }, (response) => {
            const err = window.chrome?.runtime?.lastError;
            if (err) {
              _utilLogger.log('sendMessage runtime.lastError', action, err);
            }
            cb(response);
          });
        } else {
          window.chrome.runtime.sendMessage({ action, data });
        }
      } catch (e) {
        _utilLogger.error('Chrome runtime sendMessage failed', e);
      }
    }
  }, []);

  const sendCBMessage = useCallback((action: string, param: MessageData | MessageCallback, callBack?: MessageCallback) => {
    const ids = Date.now() + Math.floor(Math.random() * 1000);
    const cparam: MessageData = typeof param === 'function' ? {} : param;
    const cb: MessageCallback | undefined = typeof param === 'function' ? param : callBack;
    _utilLogger.log('sendCBMessage calling.... isExtension', window.troveApp?.isExtension?.(), ids, action, cparam);
    if (window.troveApp?.isExtension?.()) {
      cparam.action = action;
      cparam.callbackId = ids;
      if (!window.troveApp.extCallback) window.troveApp.extCallback = {};
      window.troveApp.extCallback[ids] = (data: unknown) => {
        cb?.(data);
        delete window.troveApp!.extCallback![ids];
      };
      sendMessage('extnCallBack', cparam);
    } else {
      if (!window.troveApp?.listener) window.troveApp = { ...window.troveApp, listener: {} };
      const listener = window.troveApp.listener!;
      if (!listener.postMessage) listener.postMessage = {};
      listener.postMessage[ids] = cb!;
      window.postMessage(
        {
          from: 'troves',
          action: 'postMessageCB',
          data: { callbackId: ids, action, param: cparam },
        },
        '*'
      );
    }
  }, [sendMessage]);

  return { sendCBMessage, sendMessage };
};

/**
 * Listens for postMessage events from "troves-content" (e.g. content script).
 * - Resolves callbacks by callbackId (used with sendCBMessage from non-extension context).
 * - Optionally handles "postMessageTab" type by calling onSessions.
 * Call once at app root (e.g. in a layout or provider).
 */
export const useExtensionMessageListener = (
  options: UseExtensionMessageListenerOptions = {}
) => {
  const { onSessions, onExtensionDetected } = options;
  const onSessionsRef = useRef(onSessions);
  onSessionsRef.current = onSessions;
  const onExtensionDetectedRef = useRef(onExtensionDetected);
  onExtensionDetectedRef.current = onExtensionDetected;

  useEffect(() => {
    if (!window.troveApp) window.troveApp = {};
    if (!window.troveApp.listener) window.troveApp.listener = {};
    if (!window.troveApp.listener.postMessage) window.troveApp.listener.postMessage = {};

    window.troveApp.listener.sessions = (data: unknown) => {
      onSessionsRef.current?.(data);
    };

    const inExtension = window.troveApp?.isExtension?.();

    if (inExtension && window.chrome?.runtime?.onMessage) {
      // In extension context, messages from the background SW arrive via chrome.runtime.onMessage.
      // Content scripts are not injected into chrome-extension:// pages, so window.postMessage
      // from content scripts never fires here.
      const handleRuntimeMessage = (msg: unknown) => {
        _listenerLogger.log('Received SW runtime message', msg);
        const message = msg as RuntimeMessage;

        if (message?.action === 'extnCallBack' && message?.data?.callbackId !== undefined) {
          // Response to sendCBMessage (extnCallBack) from the SW.
          // msg shape: { action: 'extnCallBack', data: { callbackId, ...payload } }
          const fn = window.troveApp?.extCallback?.[message.data.callbackId];
          if (fn) {
            fn(message.data);
          } else {
            _listenerLogger.error('No extCallback for id', message.data.callbackId);
          }
        } else if (message?.action === 'sessions') {
          // Message from SW about sessions data (e.g. updated sessions after tab change).
          // msg shape: { action: 'sessions', data: { type, tabId, tab, csid } }
          window.troveApp?.listener?.sessions?.(message.data);
        } else if (isExtensionSwIgnoredMessage(message)) {
          // Handled by chromeAuthBridge, mfa-recaptcha-bridge, or popup listeners.
        } else {
          _listenerLogger.warn('Unhandled SW message', msg);
        }
      };

      window.chrome.runtime.onMessage.addListener(handleRuntimeMessage);
      return () => {
        window.chrome?.runtime?.onMessage.removeListener(handleRuntimeMessage);
        if (window.troveApp?.listener) {
          window.troveApp.listener.sessions = undefined;
        }
      };
    }

    // Non-extension context: receive messages from the content script via window.postMessage.
    const handleMessage = (event: MessageEvent) => {
      if (event.data == null || event.data.from !== 'troves-content') return;

      const { data } = event.data;
      _listenerLogger.log('Received MESSAGE ', data?.callbackId, (event as MessageEvent & { additionalData?: unknown }).additionalData, event.data);

      if(event.data.type === 'tabregistered') {
        if (window.troveApp) {
          _listenerLogger.log('Tab registered with content script');
          window.troveApp.hasExtension = true;
        }
        onExtensionDetectedRef.current?.();
        return;
      }

      if (data?.callbackId !== undefined) {
        const postMessageMap = window.troveApp!.listener!.postMessage!;
        const fnc = postMessageMap[data.callbackId];
        if (fnc !== undefined) {
          delete postMessageMap[data.callbackId];
          fnc(data);
        } else {
          _listenerLogger.error('No function for POST MESSAGE ', data, postMessageMap);
        }
      } else if (event.data.type === 'postMessageTab') {
        const sessionsFn = window.troveApp?.listener?.sessions;
        if (sessionsFn !== undefined) {
          sessionsFn(event.data.data?.data);
        } else {
          _listenerLogger.error('No function for postMessageTab', event.data);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (window.troveApp?.listener) {
        window.troveApp.listener.sessions = undefined;
      }
    };
  }, []);
};
